import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Key, Plus, Copy, Check, Trash2, AlertTriangle, Code, ExternalLink, BookOpen } from 'lucide-react';
import { toast } from 'react-toastify';
import { supabase } from '../../supabaseClient';
import UserLayout from './UserLayout';

const Developer = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState({
    name: '',
    permissions: 'read_only',
    rate_limit_per_minute: 60,
    rate_limit_per_day: 10000,
    expires_in_days: null
  });
  const [generatedKey, setGeneratedKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);
  const history = useHistory();

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoadingApiKeys(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Fetch API keys from FastAPI
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/v1/auth/keys`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const keys = await response.json();
        setApiKeys(keys);
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
      // Don't show error toast, API might not be deployed yet
    } finally {
      setLoadingApiKeys(false);
    }
  };

  const handleCreateApiKey = async () => {
    try {
      if (!newKeyData.name.trim()) {
        toast.error('Please enter a key name');
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/v1/auth/keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newKeyData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create API key');
      }

      const newKey = await response.json();
      setGeneratedKey(newKey.api_key);
      setApiKeys([...apiKeys, newKey]);
      toast.success('API key created successfully!');
      
      // Reset form
      setNewKeyData({
        name: '',
        permissions: 'read_only',
        rate_limit_per_minute: 60,
        rate_limit_per_day: 10000,
        expires_in_days: null
      });
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error(error.message || 'Failed to create API key');
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/v1/auth/keys/${keyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to revoke API key');
      }

      setApiKeys(apiKeys.filter(key => key.id !== keyId));
      toast.success('API key revoked successfully');
    } catch (error) {
      console.error('Error revoking API key:', error);
      toast.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    toast.success('API key copied to clipboard');
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const closeGeneratedKeyModal = () => {
    setGeneratedKey(null);
    setShowCreateModal(false);
  };

  return (
    <UserLayout>
      {/* Create API Key Modal */}
      <AnimatePresence>
        {showCreateModal && !generatedKey && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-2xl font-bold text-brand-dark mb-4">Create New API Key</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Key Name *
                  </label>
                  <input
                    type="text"
                    value={newKeyData.name}
                    onChange={(e) => setNewKeyData({ ...newKeyData, name: e.target.value })}
                    placeholder="e.g., Production Server"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Permissions
                  </label>
                  <select
                    value={newKeyData.permissions}
                    onChange={(e) => setNewKeyData({ ...newKeyData, permissions: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                  >
                    <option value="read_only">Read Only</option>
                    <option value="read_write">Read & Write</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expires In (days, optional)
                  </label>
                  <input
                    type="number"
                    value={newKeyData.expires_in_days || ''}
                    onChange={(e) => setNewKeyData({ ...newKeyData, expires_in_days: e.target.value ? parseInt(e.target.value) : null })}
                    placeholder="Leave empty for no expiration"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateApiKey}
                  className="flex-1 px-4 py-2 bg-brand text-brand-dark rounded-lg hover:bg-brand-hover transition-colors font-medium"
                >
                  Generate Key
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Generated Key Modal */}
        {generatedKey && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-md w-full"
            >
              <div className="flex items-center justify-center mb-4">
                <div className="bg-green-100 p-3 rounded-full">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-brand-dark text-center mb-2">API Key Created!</h3>
              <p className="text-center text-gray-600 mb-4">
                Copy your API key now. You won't be able to see it again!
              </p>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">
                    Store this key securely. It cannot be recovered if lost.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-xs text-gray-600 mb-2">Your API Key:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono bg-white p-2 rounded border border-gray-300 break-all">
                    {generatedKey}
                  </code>
                  <button
                    onClick={() => copyToClipboard(generatedKey)}
                    className="p-2 bg-brand text-brand-dark rounded hover:bg-brand-hover transition-colors"
                    title="Copy to clipboard"
                  >
                    {copiedKey ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                onClick={closeGeneratedKeyModal}
                className="w-full px-4 py-2 bg-brand text-brand-dark rounded-lg hover:bg-brand-hover transition-colors font-medium"
              >
                I've Saved My Key
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Code className="w-8 h-8 mr-3 text-brand" />
            Developer Portal
          </h1>
          <p className="text-gray-600 mt-2">Manage API keys and access documentation for system integration</p>
        </motion.div>

        {/* API Documentation Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-br from-brand to-brand-hover rounded-lg shadow-sm border border-gray-200 p-8 mb-6"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-brand-dark mb-2 flex items-center">
                <BookOpen className="w-6 h-6 mr-2" />
                API Documentation
              </h2>
              <p className="text-brand-dark text-opacity-80 mb-4">
                Learn how to integrate with our REST API for orders, products, and stock management. 
                Access comprehensive documentation with examples and best practices.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://partner.mhsonline.au/api/v1/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-brand-dark text-white rounded-lg font-medium hover:bg-gray-800 transition-colors shadow-md"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  View Documentation
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
                <span className="inline-flex items-center px-4 py-3 bg-white bg-opacity-50 rounded-lg text-sm font-medium text-brand-dark">
                  Base URL: https://partner.mhsonline.au/api/v1
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* API Keys Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 pb-4 border-b border-gray-200 gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <Key className="w-5 h-5 mr-2" />
                API Keys
              </h2>
              <p className="text-gray-600 text-sm mt-1">Create and manage API keys for authentication</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center px-4 py-2 bg-brand text-brand-dark rounded-lg hover:bg-brand-hover transition-colors font-medium shadow-sm"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create API Key
            </button>
          </div>

          {loadingApiKeys ? (
            <p className="text-center text-gray-500 py-8">Loading API keys...</p>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
              <Key className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-2">No API keys yet</p>
              <p className="text-sm text-gray-500">Create your first API key to start integrating with external systems</p>
            </div>
          ) : (
            <div className="space-y-4">
              {apiKeys.map((key) => (
                <div key={key.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900">{key.name}</h3>
                      <p className="text-sm text-gray-500 font-mono mt-1">{key.key_prefix}...</p>
                    </div>
                    <button
                      onClick={() => handleRevokeApiKey(key.id)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                      title="Revoke API Key"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                    <div>
                      <span className="text-gray-500">Permission:</span>
                      <p className="font-medium capitalize">{key.permissions.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <p className={`font-medium ${key.is_active ? 'text-green-600' : 'text-red-600'}`}>
                        {key.is_active ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Rate Limit:</span>
                      <p className="font-medium">{key.rate_limit_per_minute}/min</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Created:</span>
                      <p className="font-medium">{new Date(key.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  {key.last_used_at && (
                    <div className="mt-2 text-xs text-gray-500">
                      Last used: {new Date(key.last_used_at).toLocaleString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </UserLayout>
  );
};

export default Developer;
